<section
  class="flex min-h-screen flex-col p-6 pb-0"
  aria-labelledby="manage-account-heading"
>
  <div class="mx-auto flex w-full max-w-360 flex-col">
    <h1 id="manage-account-heading" class="mb-4">Manage Account</h1>

    <div class="flex w-full flex-col bg-white pb-4 shadow-sm">
      <!-- Top section: Profile + Permissions -->
      <div
        class="flex w-full"
        [ngClass]="canManageUsers() ? 'divide-x divide-gray-100' : ''"
      >
        <!-- Left: Profile card -->
        <div class="p-6" [ngClass]="canManageUsers() ? 'w-1/2' : 'w-full'">
          <!-- Avatar + name -->
          <div class="flex items-center gap-4 border-b border-gray-100 pb-5">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 font-medium text-blue-600"
              aria-hidden="true"
            >
              {{ loggedInUser()?.Name | slice: 0 : 1
              }}{{ loggedInUser()?.Name?.split(' ')?.[1] | slice: 0 : 1 }}
            </div>
            <div>
              <h2 class="text-base font-medium text-gray-800">
                {{ loggedInUser()?.Name }}
              </h2>
              <p class="mt-0.5 text-gray-600">
                {{ loggedInUser()?.Email }}
              </p>
              <span
                class="mt-1.5 inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-700"
              >
                {{ loggedInUser().Portal_Role__r?.Name }}
              </span>
            </div>
          </div>

          <!-- Meta grid -->
          <dl class="mt-5 grid grid-cols-2 gap-4" aria-label="Profile details">
            <div>
              <dt
                class="mb-1 text-[10px] tracking-widest text-gray-400 uppercase"
              >
                Account
              </dt>
              <dd class="text-gray-800">
                {{ loggedInUser()?.Account?.Name || 'N/A' }}
              </dd>
            </div>

            <div>
              <dt
                class="mb-1 text-[10px] tracking-widest text-gray-400 uppercase"
              >
                Member since
              </dt>
              <dd class="text-gray-800">
                {{ loggedInUser()?.CreatedDate | date: 'MMM d, y' }}
              </dd>
            </div>

            <div>
              <dt
                class="mb-1 text-[10px] tracking-widest text-gray-400 uppercase"
              >
                Username
              </dt>
              <dd class="truncate text-gray-800">
                {{ loggedInUser().OktaUsername__c || 'N/A' }}
              </dd>
            </div>

            <div>
              <dt
                class="mb-1 text-[10px] tracking-widest text-gray-400 uppercase"
              >
                Phone
              </dt>
              <dd class="text-gray-800">
                @if (loggedInUser()?.Phone) {
                  {{ loggedInUser().Phone | phoneNumber: 'US' }}
                } @else {
                  <span class="text-gray-400">N/A</span>
                }
              </dd>
            </div>

            <div class="col-span-2">
              <dt
                class="mb-1 text-[10px] tracking-widest text-gray-400 uppercase"
              >
                Billing address
              </dt>
              <dd class="flex items-start gap-1 text-gray-800">
                <mat-icon
                  class="mt-1 h-4! w-4! shrink-0 text-base! text-gray-400"
                  aria-hidden="true"
                  >location_on</mat-icon
                >
                @if (loggedInUser()?.Account?.BillingAddress?.street) {
                  <div>
                    <div>
                      {{ loggedInUser()?.Account?.BillingAddress?.street }}
                    </div>
                    <div>
                      {{ loggedInUser()?.Account?.BillingAddress?.city }},
                      {{ loggedInUser()?.Account?.BillingAddress?.stateCode }}
                      {{ loggedInUser()?.Account?.BillingAddress?.postalCode }}
                    </div>
                  </div>
                } @else {
                  <span class="text-gray-400">N/A</span>
                }
              </dd>
            </div>
          </dl>
        </div>

        <!-- Right: Roles & Permissions (read-only reference) -->
        @if (canManageUsers()) {
          <div class="w-1/2 p-6">
            <h3 class="font-montserrat mb-1 font-semibold">
              User roles &amp; permissions
            </h3>
            <p class="mb-5 text-sm text-gray-400">
              Each role grants a fixed set of permissions. Click edit next to a
              user to change a user's role.
            </p>

            <table
              class="w-full border-collapse text-sm"
              aria-label="User roles and permissions"
            >
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="w-2/5 pb-3 text-left text-[10px] font-medium tracking-widest text-gray-600 uppercase"
                  >
                    Permission
                  </th>
                  @for (role of portalRoles$ | async; track role.Id) {
                    <th
                      scope="col"
                      class="pb-3 text-center text-[10px] font-medium tracking-widest text-gray-600 uppercase"
                      [attr.id]="
                        'role-' + (role.Name.split(' ').join('-') | lowercase)
                      "
                    >
                      <div>{{ role.Name }}</div>
                    </th>
                  }
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                @for (permission of permissionList; track permission.key) {
                  <tr class="group odd:bg-gray-50/50">
                    <th
                      scope="row"
                      class="py-2 pr-4 text-left text-sm font-normal text-gray-800 group-hover:text-gray-800"
                    >
                      {{ permission.label }}
                    </th>
                    @for (role of portalRoles$ | async; track role.Id) {
                      <td class="py-2 text-center">
                        @if (role?.Permissions__c?.includes(permission.key)) {
                          <span
                            class="mx-auto inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100"
                            aria-label="Has access"
                          >
                            <mat-icon
                              class="h-3! w-3! text-xs! text-green-600"
                              aria-hidden="true"
                              >check</mat-icon
                            >
                          </span>
                        } @else {
                          <span
                            class="mx-auto inline-flex h-5 w-5 items-center justify-center"
                            aria-label="No access"
                          >
                            <mat-icon
                              class="h-3! w-3! text-xs! text-gray-400"
                              aria-hidden="true"
                              >remove</mat-icon
                            >
                          </span>
                        }
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>

            <!-- Legend -->
            <div
              class="mt-4 flex items-center gap-4 border-t border-gray-100 pt-4"
            >
              <div class="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span
                  class="inline-flex h-4 w-4 items-center justify-center rounded-full bg-green-100"
                  aria-hidden="true"
                >
                  <mat-icon class="h-3! w-3! text-xs! text-green-600"
                    >check</mat-icon
                  >
                </span>
                Has access
              </div>
              <div class="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span
                  class="inline-flex h-4 w-4 items-center justify-center"
                  aria-hidden="true"
                >
                  <mat-icon class="h-3! w-3! text-xs! text-gray-400"
                    >remove</mat-icon
                  >
                </span>
                No access
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Bottom: Manage Users -->
      @if (canManageUsers()) {
        <section
          class="min-h-52 overflow-x-auto border-t border-gray-200"
          aria-labelledby="manage-users-heading"
        >
          <!-- Toolbar -->
          <div
            class="flex w-full items-center justify-between border-b border-gray-100 bg-[#f7f7f7] px-6 py-4"
          >
            <h2 id="manage-users-heading" class="text-lg font-medium uppercase">
              Manage users
              <span class="ml-1.5 font-normal text-gray-400 lowercase">
                {{
                  (users$ | async).length > 0
                    ? '(' + (users$ | async).length + ' total)'
                    : ''
                }}
              </span>
            </h2>

            <div class="flex items-center gap-2">
              <!-- Search -->
              <label for="manage-users-search" class="sr-only">
                Search users
              </label>
              <div class="relative w-80">
                <mat-icon
                  class="pointer-events-none absolute top-1/2 left-3 flex! h-4! w-4! -translate-y-1/2 items-center! justify-center! text-lg! text-black"
                  aria-hidden="true"
                  >search</mat-icon
                >
                <input
                  id="manage-users-search"
                  type="search"
                  aria-label="Search users"
                  placeholder="Search users..."
                  class="comcast-input mt-0! pl-9 text-sm!"
                  autocomplete="off"
                  [(ngModel)]="userSearchTerm"
                  (ngModelChange)="applyUserSearchFilter()"
                />
              </div>

              <!-- Role filter -->
              <div class="relative">
                <button
                  id="roleFilter-trigger"
                  type="button"
                  aria-label="Filter by role"
                  class="comcast-select-trigger min-w-32"
                  [class.comcast-select-trigger--open]="isRoleFilterOpen()"
                  [class.comcast-select-trigger--filled]="!!selectedRoleFilter"
                  [attr.aria-expanded]="isRoleFilterOpen()"
                  aria-haspopup="listbox"
                  aria-controls="roleFilter-listbox"
                  (click)="toggleRoleFilter($event)"
                >
                  <span>
                    {{ selectedRoleFilter || 'All roles' }}
                  </span>
                  <mat-icon class="comcast-select-icon" aria-hidden="true">
                    {{
                      isRoleFilterOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                    }}
                  </mat-icon>
                </button>

                @if (isRoleFilterOpen()) {
                  <!-- a11y listbox pattern: docs/accessibility/custom-listbox.md -->
                  <div
                    id="roleFilter-listbox"
                    class="comcast-select-panel"
                    [class.comcast-select-panel--open]="isRoleFilterOpen()"
                    role="listbox"
                    aria-label="Filter by role"
                    (click)="$event.stopPropagation()"
                  >
                    <div class="comcast-select-options">
                      <div
                        class="comcast-select-option"
                        [class.comcast-select-option--selected]="
                          !selectedRoleFilter
                        "
                        role="option"
                        tabindex="0"
                        [attr.aria-selected]="!selectedRoleFilter"
                        (click)="selectRoleFilter('', $event)"
                        (keydown.enter)="selectRoleFilter('', $event)"
                        (keydown.space)="selectRoleFilter('', $event)"
                      >
                        All roles
                      </div>
                      @for (role of portalRoles$ | async; track role.Id) {
                        @if (role.Name) {
                          <div
                            class="comcast-select-option"
                            [class.comcast-select-option--selected]="
                              selectedRoleFilter === role.Name
                            "
                            role="option"
                            tabindex="0"
                            [attr.aria-selected]="
                              selectedRoleFilter === role.Name
                            "
                            (click)="selectRoleFilter(role.Name, $event)"
                            (keydown.enter)="
                              selectRoleFilter(role.Name, $event)
                            "
                            (keydown.space)="
                              selectRoleFilter(role.Name, $event)
                            "
                          >
                            {{ role.Name }}
                          </div>
                        }
                      }
                    </div>
                  </div>
                }
              </div>

              <!-- Per page filter -->
              <div class="relative">
                <button
                  id="pageSizeFilter-trigger"
                  type="button"
                  aria-label="Items per page"
                  class="comcast-select-trigger min-w-32"
                  [class.comcast-select-trigger--open]="isPageSizeDropdownOpen()"
                  [attr.aria-expanded]="isPageSizeDropdownOpen()"
                  aria-haspopup="listbox"
                  aria-controls="pageSizeFilter-listbox"
                  (click)="togglePageSizeDropdown($event)"
                >
                  <span>Per page: {{ pageSize() }}</span>
                  <mat-icon class="comcast-select-icon" aria-hidden="true">
                    {{
                      isPageSizeDropdownOpen()
                        ? 'arrow_drop_up'
                        : 'arrow_drop_down'
                    }}
                  </mat-icon>
                </button>

                @if (isPageSizeDropdownOpen()) {
                  <div
                    id="pageSizeFilter-listbox"
                    class="comcast-select-panel"
                    [class.comcast-select-panel--open]="
                      isPageSizeDropdownOpen()
                    "
                    role="listbox"
                    aria-label="Items per page options"
                    (click)="$event.stopPropagation()"
                  >
                    <div class="comcast-select-options">
                      @for (option of pageSizeOptions; track option) {
                        <div
                          class="comcast-select-option"
                          [class.comcast-select-option--selected]="
                            pageSize() === option
                          "
                          role="option"
                          tabindex="0"
                          [attr.aria-selected]="pageSize() === option"
                          (click)="onPageSizeChange(option)"
                          (keydown.enter)="onPageSizeChange(option)"
                          (keydown.space)="onPageSizeChange(option)"
                        >
                          {{ option }} per page
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>

              <!-- Invite -->
              <button
                type="button"
                class="btn-primary"
                (click)="onCreateEditUser()"
              >
                <mat-icon
                  class="mr-2 h-4! w-4! text-base! leading-none!"
                  aria-hidden="true"
                  >person_add</mat-icon
                >
                Invite user
              </button>
            </div>
          </div>

          <!-- Users Table -->
          <table
            mat-table
            matSort
            [dataSource]="pagedUsers()"
            matSortActive="FirstName"
            matSortDirection="asc"
            class="w-full min-w-[76rem]"
            aria-labelledby="manage-users-heading"
          >
            <!-- Avatar column -->
            <ng-container matColumnDef="Number">
              <th
                mat-header-cell
                *matHeaderCellDef
                aria-label="Avatar"
                class="font-montserrat w-14! py-2.5! pr-3! pl-6! text-left text-[10px] font-semibold tracking-widest text-gray-600 uppercase"
              ></th>
              <td
                mat-cell
                *matCellDef="let user"
                class="w-14! py-3! pr-3! pl-6!"
              >
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600"
                  aria-hidden="true"
                >
                  {{ user.FirstName | slice: 0 : 1
                  }}{{ user.LastName | slice: 0 : 1 }}
                </div>
              </td>
            </ng-container>

            <!-- User Info column -->
            <ng-container matColumnDef="UserInfo">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="font-montserrat px-3! py-2.5! text-left font-semibold tracking-widest uppercase"
              >
                User
              </th>
              <td mat-cell *matCellDef="let user" class="px-3! py-3!">
                <div class="font-medium text-gray-800">
                  {{ user.LastName }}, {{ user.FirstName }}
                </div>
                <div
                  class="mt-0.5 flex items-center gap-1 text-sm text-gray-400"
                >
                  <mat-icon
                    class="h-3! w-3! text-xs! leading-none!"
                    aria-hidden="true"
                    >mail</mat-icon
                  >
                  {{ user.Email }}
                </div>
              </td>
            </ng-container>

            <!-- Username column -->
            <ng-container matColumnDef="Username">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="font-montserrat px-3! py-2.5! text-left font-semibold tracking-widest uppercase"
              >
                Username
              </th>
              <td mat-cell *matCellDef="let user" class="px-3! py-3!">
                <span class="truncate text-sm text-gray-600">{{
                  user.OktaUsername__c
                }}</span>
              </td>
            </ng-container>

            <!-- Role column -->
            <ng-container matColumnDef="Role">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="font-montserrat w-24! px-3! py-2.5! text-left font-semibold tracking-widest uppercase"
              >
                Role
              </th>
              <td mat-cell *matCellDef="let user" class="w-24! px-3! py-3!">
                @if (user?.Portal_Role__r?.Name) {
                  <a
                    [routerLink]="[]"
                    [fragment]="
                      'role-' +
                      (user.Portal_Role__r?.Name.split(' ').join('-')
                        | lowercase)
                    "
                    [attr.aria-label]="
                      'View ' + user.Portal_Role__r?.Name + ' role permissions'
                    "
                    class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-sm text-gray-800 transition-colors hover:border-gray-300"
                  >
                    {{ user.Portal_Role__r?.Name }}
                  </a>
                }
              </td>
            </ng-container>

            <!-- Created Date column -->
            <ng-container matColumnDef="CreatedDate">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="font-montserrat w-36! px-3! py-2.5! text-left font-semibold tracking-widest uppercase"
              >
                Created
              </th>
              <td mat-cell *matCellDef="let user" class="w-36! px-3! py-3!">
                <span class="text-xs text-gray-600">{{
                  user.CreatedDate | date: 'MMM d, y'
                }}</span>
              </td>
            </ng-container>

            <!-- Last Login column -->
            <ng-container matColumnDef="LastPortalLogin">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="font-montserrat w-40! px-3! py-2.5! text-left font-semibold tracking-widest uppercase"
              >
                Last login
              </th>
              <td mat-cell *matCellDef="let user" class="w-40! px-3! py-3!">
                <div
                  class="flex items-center gap-1.5 text-xs whitespace-nowrap"
                >
                  @if (user?.LoggedInWithOkta__c) {
                    @if (user?.Last_Portal_Login_At__c) {
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full bg-green-600"
                        aria-hidden="true"
                      ></span>
                      <span class="text-gray-600">{{
                        user.Last_Portal_Login_At__c | date: 'MMM d, y'
                      }}</span>
                    } @else {
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300"
                        aria-hidden="true"
                      ></span>
                      <span class="text-gray-400 italic"
                        >History unavailable</span
                      >
                    }
                  } @else {
                    <span
                      class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"
                      aria-hidden="true"
                    ></span>
                    <span class="text-gray-600">Never logged in</span>
                  }
                </div>
              </td>
            </ng-container>

            <!-- Actions column -->
            <ng-container matColumnDef="Actions">
              <th
                mat-header-cell
                *matHeaderCellDef
                aria-label="Actions"
                class="font-montserrat w-44! min-w-44! py-2.5! pr-6! pl-3! text-right font-semibold tracking-widest uppercase"
              ></th>
              <td
                mat-cell
                *matCellDef="let user"
                class="w-44! min-w-44! py-3! pr-6! pl-3!"
              >
                <div
                  class="flex min-w-max flex-nowrap items-center justify-end gap-1"
                >
                  <button
                    (click)="onCreateEditUser(user)"
                    [attr.aria-label]="
                      'Edit ' + user.FirstName + ' ' + user.LastName
                    "
                    [disabled]="isDemoAccount()"
                    [matTooltip]="
                      isDemoAccount()
                        ? 'Not available for demo accounts'
                        : 'Edit User'
                    "
                    class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50"
                    [class.opacity-50]="isDemoAccount()"
                    [class.cursor-not-allowed]="isDemoAccount()"
                  >
                    <mat-icon
                      class="h-4! w-4! text-base! leading-none!"
                      aria-hidden="true"
                      >edit</mat-icon
                    >
                  </button>

                  <span
                    *ngxPermissionsOnly="Permission.NITEL_INTERNAL"
                    class="inline-flex items-center gap-1"
                  >
                    <button
                      (click)="onDeactivateUser(user)"
                      [attr.aria-label]="
                        'Remove ' + user.FirstName + ' ' + user.LastName
                      "
                      [disabled]="isDemoAccount()"
                      [matTooltip]="
                        isDemoAccount()
                          ? 'Not available for demo accounts'
                          : 'Remove user'
                      "
                      class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-red-500 transition-colors hover:border-red-300 hover:bg-red-50"
                      [class.opacity-50]="isDemoAccount()"
                      [class.cursor-not-allowed]="isDemoAccount()"
                    >
                      <mat-icon
                        class="h-4! w-4! text-base! leading-none!"
                        aria-hidden="true"
                        >delete</mat-icon
                      >
                    </button>
                    @if (!user.LoggedInWithOkta__c) {
                      <button
                        (click)="onResendInvite(user)"
                        [attr.aria-label]="
                          'Resend invite for ' +
                          user.FirstName +
                          ' ' +
                          user.LastName
                        "
                        [disabled]="isDemoAccount()"
                        [matTooltip]="
                          isDemoAccount()
                            ? 'Not available for demo accounts'
                            : 'Resend Invite'
                        "
                        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50"
                        [class.opacity-50]="isDemoAccount()"
                        [class.cursor-not-allowed]="isDemoAccount()"
                      >
                        <mat-icon
                          class="h-4! w-4! text-base! leading-none!"
                          aria-hidden="true"
                          >mail</mat-icon
                        >
                      </button>
                    }
                    @if (user.LoggedInWithOkta__c) {
                      <button
                        (click)="onSendPasswordReset(user)"
                        aria-label="Send password reset"
                        [disabled]="isDemoAccount()"
                        [matTooltip]="
                          isDemoAccount()
                            ? 'Not available for demo accounts'
                            : 'Send Password Reset'
                        "
                        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-blue-600 transition-colors hover:border-blue-300 hover:bg-blue-50"
                        [class.opacity-50]="isDemoAccount()"
                        [class.cursor-not-allowed]="isDemoAccount()"
                      >
                        <mat-icon
                          class="h-4! w-4! text-base! leading-none!"
                          aria-hidden="true"
                          >email</mat-icon
                        >
                      </button>
                    }
                    @if (
                      user.LoggedInWithOkta__c && user.Last_Portal_Login_At__c
                    ) {
                      <button
                        (click)="onUnlockUser(user)"
                        aria-label="Unlock user"
                        [disabled]="isDemoAccount()"
                        [matTooltip]="
                          isDemoAccount()
                            ? 'Not available for demo accounts'
                            : 'Unlock User'
                        "
                        class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-amber-500 transition-colors hover:border-amber-300 hover:bg-amber-50"
                        [class.opacity-50]="isDemoAccount()"
                        [class.cursor-not-allowed]="isDemoAccount()"
                      >
                        <mat-icon
                          class="h-4! w-4! text-base! leading-none!"
                          aria-hidden="true"
                          >lock_open</mat-icon
                        >
                      </button>
                    }
                  </span>
                </div>
              </td>
            </ng-container>

            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns"
              class="border-b border-gray-100 bg-white"
            ></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns"
              class="border-b border-gray-100 transition-colors hover:bg-gray-50"
            ></tr>
          </table>

          <!-- Empty states -->
          @if ((users$ | async).length === 0) {
            <p
              role="status"
              aria-live="polite"
              class="py-8 text-center text-gray-400"
            >
              No users found. Please invite a user.
            </p>
          }

          @if (
            (users$ | async).length > 0 &&
            !dataSource.filteredData.length &&
            dataSource.filter.length > 0
          ) {
            <p
              role="status"
              aria-live="polite"
              class="py-8 text-center text-gray-400"
            >
              No users found
              @if (userSearchTerm && selectedRoleFilter) {
                based on your filter and search selection.
              } @else if (userSearchTerm) {
                based on your search.
              } @else if (selectedRoleFilter) {
                based on your filter selection.
              }
            </p>
          }

          <!-- Paginator -->
          @if ((users$ | async).length > 0) {
            <div class="border-t border-gray-100 py-4">
              <app-pagination
                [currentPage]="effectivePage()"
                [totalPages]="totalPages()"
                [itemsPerPage]="pageSize()"
                (pageChange)="onPageChange($event)"
              ></app-pagination>
            </div>
          }
        </section>
      }
    </div>
  </div>
</section>
